import { ValidationRule } from '../types/validationTypes';
import { getDrawerByCuit } from '../services/GuaranteesService';
import { FormValues } from '../pages/MonetizationNew';
import React from 'react';

export const validateRequired = (value: any): string | null => {
  if (value === null || value === undefined || value === '' || 
      (Array.isArray(value) && value.length === 0)) {
    return 'Este campo es obligatorio';
  }
  return null;
};

export const validateMinLength = (value: string, minLength: number): string | null => {
  if (value && value.length < minLength) {
    return `Debe tener al menos ${minLength} caracteres`;
  }
  return null;
};

export const validateMaxLength = (value: string, maxLength: number): string | null => {
  if (value && value.length > maxLength) {
    return `No puede tener más de ${maxLength} caracteres`;
  }
  return null;
};

export const validateNumericRange = (value: number, min?: number, max?: number): string | null => {
  if (value !== null && value !== undefined) {
    if (min !== undefined && value < min) {
      return `El valor debe ser mayor o igual a ${min}`;
    }
    if (max !== undefined && value > max) {
      return `El valor debe ser menor o igual a ${max}`;
    }
  }
  return null;
};

export const validateNumeric = (value: any): string | null => {
  if (value !== null && value !== undefined && value !== '' && isNaN(Number(value))) {
    return 'Debe ser un número válido';
  }
  return null;
};

export const validateInteger = (value: any): string | null => {
  if (value !== null && value !== undefined && value !== '') {
    const num = Number(value);
    if (isNaN(num) || !Number.isInteger(num)) {
      return 'Debe ser un número entero válido';
    }
  }
  return null;
};

export const validateDate = (value: string): string | null => {
  if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return 'Formato de fecha inválido';
  }
  
  if (value) {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return 'Fecha inválida';
    }
  }
  return null;
};

export const validateDateNotBefore = (value: string, compareDate: string, fieldName: string): string | null => {
  if (value && compareDate) {
    const date1 = new Date(value);
    const date2 = new Date(compareDate);
    
    if (date1 < date2) {
      return `La fecha no puede ser anterior a ${fieldName}`;
    }
  }
  return null;
};

export const validateCuit = (value: string): string | null => {
  if (!value) return null;
  
  const cuit = value.replace(/[-\s]/g, '');
  
  if (!/^\d{11}$/.test(cuit)) {
    return 'El CUIT debe tener 11 dígitos';
  }
  
  const digits = cuit.split('').map(Number);
  const multipliers = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += digits[i] * multipliers[i];
  }
  
  const remainder = sum % 11;
  const checkDigit = remainder < 2 ? remainder : 11 - remainder;
  
  if (checkDigit !== digits[10]) {
    return 'CUIT inválido';
  }
  
  return null;
};

export const validateField = (value: any, rules: ValidationRule): string | null => {
  if (rules.required) {
    const requiredError = validateRequired(value);
    if (requiredError) return requiredError;
  }
  
  if (!rules.required && (value === null || value === undefined || value === '')) {
    return null;
  }
  
  if (rules.minLength !== undefined) {
    const minLengthError = validateMinLength(value, rules.minLength);
    if (minLengthError) return minLengthError;
  }
  
  if (rules.maxLength !== undefined) {
    const maxLengthError = validateMaxLength(value, rules.maxLength);
    if (maxLengthError) return maxLengthError;
  }
  
  if (rules.min !== undefined || rules.max !== undefined) {
    const numericError = validateNumeric(value);
    if (numericError) return numericError;
    
    const rangeError = validateNumericRange(Number(value), rules.min, rules.max);
    if (rangeError) return rangeError;
  }
  
  if (rules.pattern && value) {
    if (!rules.pattern.test(value)) {
      return 'Formato inválido';
    }
  }
  
  if (rules.custom) {
    const customError = rules.custom(value);
    if (customError) return customError;
  }
  
  return null;
};

export interface DrawerValidationParams {
  cuit: string;
  formValues: FormValues;
  updateFormValues: (updates: Partial<FormValues>) => void;
  setDrawerError: (error: string) => void;
  setValidatingDrawer: (validating: boolean) => void;
  lastValidatedCuit: string;
  setLastValidatedCuit: (cuit: string) => void;
  drawerManuallyEdited: boolean;
  setDrawerManuallyEdited: (edited: boolean) => void;
  debounceTimeoutRef?: React.MutableRefObject<NodeJS.Timeout | null>;
}

export interface DrawerValidationResult {
  success: boolean;
  message?: string;
}

export const validateDrawerByCuit = async ({
  cuit,
  formValues,
  updateFormValues,
  setDrawerError,
  setValidatingDrawer,
  lastValidatedCuit,
  setLastValidatedCuit,
  drawerManuallyEdited,
  setDrawerManuallyEdited,
  debounceTimeoutRef
}: DrawerValidationParams): Promise<DrawerValidationResult> => {
  setDrawerError('');
  
  // Si el CUIT cambió, reiniciar el estado de edición manual
  if (cuit !== lastValidatedCuit) {
    setDrawerManuallyEdited(false);
  }
  
  updateFormValues({ 
    drawer: { 
      cuit: cuit,
      company_name: formValues.drawer?.company_name || ''
    } 
  });

  if (cuit.length === 11) {
    // Limpiar timeout previo
    if (debounceTimeoutRef?.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Crear nuevo timeout con delay de 500ms
    return new Promise((resolve) => {
      const timeoutId = setTimeout(async () => {
        setValidatingDrawer(true);
        try {
          const response = await getDrawerByCuit(cuit);
          
          if (response.success && 'data' in response && response.data) {
            const { drawer_name } = response.data;
            
            setLastValidatedCuit(cuit);
            
            const currentCompanyName = formValues.drawer?.company_name || '';
            
            if (currentCompanyName && currentCompanyName.toLowerCase() !== drawer_name?.toLowerCase()) {
              if (drawerManuallyEdited) {
                const editMessage = `Se editará el nombre del librador a "${currentCompanyName}"`;
                setDrawerError(editMessage);
                resolve({ success: true, message: editMessage });
              } else {
                const errorMessage = `El CUIT del librador ingresado no coincide con el nombre. El nombre almacenado es: ${drawer_name}`;
                setDrawerError(errorMessage);
                
                updateFormValues({ 
                  drawer: { 
                    cuit: cuit,
                    company_name: drawer_name
                  } 
                });
                
                resolve({ success: false, message: errorMessage });
              }
            } else {
              updateFormValues({ 
                drawer: { 
                  cuit: cuit,
                  company_name: drawer_name
                } 
              });
              
              resolve({ success: true });
            }
          } else {
            setLastValidatedCuit(cuit);
            resolve({ success: true });
          }
        } catch (error) {
          const errorMessage = 'Error al validar el CUIT';
          setDrawerError(errorMessage);
          console.error('Error validating drawer:', error);
          resolve({ success: false, message: errorMessage });
        } finally {
          setValidatingDrawer(false);
        }
      }, 500);
      
      if (debounceTimeoutRef) {
        debounceTimeoutRef.current = timeoutId;
      }
    });
  }
  
  return { success: true };
};